import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

export function Lightbox({
  url,
  type = 'image',
  onClose,
}: {
  url: string | null
  type?: 'image' | 'video'
  onClose: () => void
}) {
  useEffect(() => {
    if (!url) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopImmediatePropagation()
        onClose()
      }
    }
    document.addEventListener('keydown', handleKey, true)
    return () => document.removeEventListener('keydown', handleKey, true)
  }, [url, onClose])

  // Block pointerdown/mousedown from reaching Radix DismissableLayer
  useEffect(() => {
    if (!url) return
    const stop = (e: Event) => {
      const root = document.getElementById('lightbox-root')
      if (root?.contains(e.target as Node)) {
        e.stopPropagation()
      }
    }
    document.addEventListener('pointerdown', stop, true)
    document.addEventListener('mousedown', stop, true)
    return () => {
      document.removeEventListener('pointerdown', stop, true)
      document.removeEventListener('mousedown', stop, true)
    }
  }, [url])

  if (!url) return null

  return createPortal(
    <div
      id="lightbox-root"
      className={cn(
        'fixed inset-0 z-100 flex items-center justify-center bg-black/90 pointer-events-auto',
        'animate-in fade-in duration-200 cursor-pointer'
      )}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onClose()
        }}
        className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center text-white/60 hover:text-white transition-colors z-10"
        aria-label="Close lightbox"
      >
        <X size={20} />
      </button>
      <div
        className="animate-in slide-in-from-bottom-4 fade-in duration-300 ease-out cursor-default"
        onClick={(e) => e.stopPropagation()}
      >
        {type === 'video' ? (
          <video
            src={url}
            controls
            autoPlay
            loop
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg"
          />
        ) : (
          <img
            src={url}
            alt=""
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg"
            draggable={false}
          />
        )}
      </div>
    </div>,
    document.body
  )
}
